// @ts-strict-ignore
import React, { useMemo } from 'react'

import { getScannerLabel } from 'mobile_web/modules/scanner/functions'
import ScannerIcon from 'mobile_web/modules/scanner/components/parts/ScannerIcon'
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'

import { Button, Text, GridRow, GridCol } from '@mytours/components'
import { getDirMode } from '../../../platform'
import strings from '../../../../strings'

import './styles.less'

interface Props {
	hasPrev?: boolean
	leftButtonText?: string
	onPrevClick?: () => void

	hasMap?: boolean
	onMapClick?: () => void

	keypadEnabled?: boolean
	qrEnabled?: boolean
	onScanClick?: () => void

	hasNext?: boolean
	rightButtonText?: string
	onNextClick?: () => void
	onFinishClick?: () => void

	collectionType: GetCollectionItemsIndexResponseData.ItemSubtypeEnum
}

const TourStopFooter: React.FC<Props> = ({
	hasPrev,
	leftButtonText = strings.tour_previous,
	onPrevClick,
	hasMap,
	onMapClick,
	onScanClick,
	hasNext,
	rightButtonText = strings.tour_next,
	onNextClick,
	onFinishClick,
	keypadEnabled,
	qrEnabled,
	collectionType,
}) => {
	const ltrLeft = getDirMode() === 'rtl' ? 'right' : 'left'
	const prevButton = useMemo(
		() =>
			hasPrev ? (
				<Button className="tour-stop-action" variant="clear" onClick={onPrevClick} aria-label={leftButtonText || ''}>
					<Text variant="inherit">
						<i className={`fal fa-chevron-${ltrLeft} tour-stop-action-chevron tour-stop-action-chevron-prev`} />
						{leftButtonText}
					</Text>
				</Button>
			) : null,
		[hasPrev, leftButtonText, onPrevClick],
	)

	const mapButton = useMemo(
		() =>
			hasMap ? (
				<GridCol className="tour-action-column">
					<Button className="tour-stop-action" variant="clear" onClick={onMapClick} aria-label={strings.open_map}>
						<i className="tour-stop-action-icon fal fa-map" />
					</Button>
				</GridCol>
			) : null,
		[hasMap, onMapClick],
	)

	const scanButton = useMemo(
		() =>
			keypadEnabled || qrEnabled ? (
				<GridCol className="tour-action-column">
					<Button
						className="tour-stop-action"
						variant="clear"
						onClick={onScanClick}
						aria-label={getScannerLabel({ keypadEnabled, qrEnabled, searchEnabled: false })}
					>
						<ScannerIcon
							keypadEnabled={keypadEnabled}
							qrEnabled={qrEnabled}
							searchEnabled={false}
							className="tour-stop-action-icon"
						/>
					</Button>
				</GridCol>
			) : null,
		[keypadEnabled, qrEnabled, onScanClick],
	)

	const ltrRight = getDirMode() === 'rtl' ? 'left' : 'right'
	const nextButton = useMemo(
		() =>
			hasNext ? (
				<Button className="tour-stop-action" variant="clear" onClick={onNextClick} aria-label={rightButtonText || ''}>
					<Text variant="inherit" className="text">
						{rightButtonText}
						<i className={`fal fa-chevron-${ltrRight} tour-stop-action-chevron tour-stop-action-chevron-next`} />
					</Text>
				</Button>
			) : (
				<Button className="tour-stop-action" variant="clear" onClick={onFinishClick} aria-label={strings.finish || ''}>
					<Text variant="inherit" className="text">
						{strings.finish}
					</Text>
				</Button>
			),
		[hasNext, rightButtonText, onNextClick],
	)

	if (collectionType !== GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour) {
		return null
	}

	return (
		<GridRow className="tour-stop-footer" dir={getDirMode()}>
			<GridCol className="tour-action-column">{prevButton}</GridCol>
			{mapButton}
			{scanButton}
			<GridCol className="tour-action-column">{nextButton}</GridCol>
		</GridRow>
	)
}

export default TourStopFooter
